import {Component, DestroyRef, effect, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {ManagerSettings} from '../../../../classes/models/employee/ManagerSettings';
import {DataService} from '../../../../services/store/data.service';
import {EmployeeManagerSettingsService} from '../../../../services/http/employee/employee-manager-settings.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Data required to open the manager settings form modal. */
export interface ManagerSettingsFormData {
  employeePositionId: EmployeePositionId;
  previousManagerSettings?: ManagerSettings;
  weekExists: boolean;
}

/**
 * This component handles creating and updating manager settings.
 */
@Component({
  selector: 'app-manager-settings-form',
  templateUrl: './manager-settings-form.component.html',
  styleUrls: ['./manager-settings-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class ManagerSettingsFormComponent {

  private readonly destroyRef = inject(DestroyRef);
  private readonly employeeManagerSettingsService = inject(EmployeeManagerSettingsService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly dataService = inject(DataService);

  /** A modal component of creating manager settings form. */
  readonly modalComponent = viewChild.required<ModalComponent>('modalComponent');
  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined!);
  /** Previous manager settings. */
  readonly previousManagerSettings = signal<ManagerSettings | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal<boolean>(false);
  /** `True` if a request to backend is in progress. */
  readonly loading = signal(false);
  /** The first date, from which input data can be edited. */
  readonly editDate = signal<string | undefined>(this.dataService.editDate);
  /** A form value of a start date, from which the manager settings will take effect. */
  readonly startDate = linkedSignal({
    source: this.previousManagerSettings,
    computation: (prev) => prev?.startDate,
  });
  /** A form value, which indicates if a manager does sales. */
  readonly canDoSales = linkedSignal({
    source: this.previousManagerSettings,
    computation: (prev) => prev?.canDoSales,
  });

  static open(modalService: NgbModal, data: ManagerSettingsFormData): NgbModalRef {
    const modalRef = modalService.open(ManagerSettingsFormComponent);
    const instance = modalRef.componentInstance as ManagerSettingsFormComponent;
    instance.employeePositionId.set(data.employeePositionId);
    instance.previousManagerSettings.set(data.previousManagerSettings);
    instance.weekExists.set(data.weekExists);
    return modalRef;
  }

  /**
   * Sends request to backend to add a new manager setting.
   */
  onSubmit(): void {
    this.loading.set(true);
    const managerSettings = new ManagerSettings(this.startDate(), this.canDoSales());

    if (this.weekExists()) {
      this.editManagerSettings(managerSettings);
    } else {
      this.addManagerSettings(managerSettings);
    }
  }

  private addManagerSettings(managerSettings: ManagerSettings): void {
    this.employeeManagerSettingsService.addManagerSettings(this.employeePositionId(), managerSettings)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Manager settings has been successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editManagerSettings(managerSettings: ManagerSettings): void {
    this.employeeManagerSettingsService.editManagerSettings(this.employeePositionId(), managerSettings)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Manager settings has been successfully updated');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}